import {
  LoginCredentials,
  RegisterPayload,
  ResetPasswordPayload,
} from "@/types";

export const EMAIL_ACCOUNT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_ACCOUNT_REGEX = /^(0[35789])[0-9]{8}$/;

export type AccountKind = "email" | "phone";

export interface ParsedAccount {
  kind: AccountKind;
  value: string;
}

export const normalizeAccount = (value: string) =>
  value.trim().replace(/\s+/g, "");

export const normalizePhoneNumber = (value: string) =>
  normalizeAccount(value).replace(/[^0-9]/g, "");

export const isEmailAccount = (value: string) =>
  EMAIL_ACCOUNT_REGEX.test(normalizeAccount(value).toLowerCase());

export const isPhoneAccount = (value: string) =>
  PHONE_ACCOUNT_REGEX.test(normalizePhoneNumber(value));

export const isValidAccount = (value: string) =>
  isEmailAccount(value) || isPhoneAccount(value);

export const parseAccount = (value: string): ParsedAccount | null => {
  const normalized = normalizeAccount(value);

  if (isEmailAccount(normalized)) {
    return {
      kind: "email",
      value: normalized.toLowerCase(),
    };
  }

  if (isPhoneAccount(normalized)) {
    return {
      kind: "phone",
      value: normalized,
    };
  }

  return null;
};

export const validateAccountValue = (value: string) => {
  if (!normalizeAccount(value)) {
    return "Vui lòng nhập số điện thoại hoặc email.";
  }

  if (!isValidAccount(value)) {
    return "Số điện thoại hoặc email không hợp lệ.";
  }

  return null;
};

export const validatePhoneValue = (value: string) => {
  if (!normalizePhoneNumber(value)) {
    return "Vui lòng nhập số điện thoại.";
  }

  if (!isPhoneAccount(value)) {
    return "Số điện thoại không hợp lệ.";
  }

  return null;
};

export const validatePasswordValue = (value: string) => {
  if (!value.trim()) {
    return "Vui lòng nhập mật khẩu.";
  }

  if (value.length < 6 || value.length > 20) {
    return "Mật khẩu phải từ 6 đến 20 ký tự.";
  }

  if (!/[A-Z]/.test(value)) {
    return "Mật khẩu phải có ít nhất 1 chữ cái viết hoa.";
  }

  if (!/[a-z]/.test(value)) {
    return "Mật khẩu phải có ít nhất 1 chữ cái viết thường.";
  }

  if (!/\d/.test(value)) {
    return "Mật khẩu phải có ít nhất 1 chữ số.";
  }

  return null;
};

export const buildLoginPayload = (credentials: LoginCredentials) => {
  const parsed = parseAccount(credentials.account);

  if (!parsed) {
    throw new Error("Tài khoản đăng nhập không hợp lệ.");
  }

  return {
    password: credentials.password,
    deviceName: credentials.deviceName,
    ...(parsed.kind === "email"
      ? { email: parsed.value }
      : { phone: parsed.value }),
  };
};

export const buildPhoneOtpPayload = (phone: string) => {
  const normalizedPhone = normalizePhoneNumber(phone);
  const phoneError = validatePhoneValue(normalizedPhone);

  if (phoneError) {
    throw new Error(phoneError);
  }

  return {
    phone: normalizedPhone,
  };
};

export const buildRegisterPayload = (payload: RegisterPayload) => {
  const parsed = parseAccount(payload.account);

  if (!parsed) {
    throw new Error("Tài khoản đăng ký không hợp lệ.");
  }

  return {
    fullName: payload.fullName.trim(),
    password: payload.password,
    confirmPassword: payload.confirmPassword,
    ...(parsed.kind === "email"
      ? { email: parsed.value }
      : { phone: parsed.value }),
  };
};

export const buildForgotPasswordPayload = (account: string) => {
  const parsed = parseAccount(account);

  if (!parsed) {
    throw new Error("Tài khoản không hợp lệ.");
  }

  return parsed.kind === "email"
    ? { email: parsed.value }
    : { phone: parsed.value };
};

export const buildResetPasswordPayload = (payload: ResetPasswordPayload) => ({
  ...buildForgotPasswordPayload(payload.account),
  otp: payload.otp,
  newPassword: payload.newPassword,
  confirmPassword: payload.confirmPassword,
  new_password: payload.newPassword,
  confirm_password: payload.confirmPassword,
});

export const maskAccount = (account: string) => {
  const parsed = parseAccount(account);

  if (!parsed) {
    return account;
  }

  if (parsed.kind === "email") {
    const [localPart = "", domain = ""] = parsed.value.split("@");
    const visibleLocal = localPart.slice(0, 2);
    return `${visibleLocal}${"*".repeat(Math.max(localPart.length - 2, 2))}@${domain}`;
  }

  return `${parsed.value.slice(0, 3)}****${parsed.value.slice(-3)}`;
};
